import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { 
  Users, 
  Printer, 
  Ticket, 
  AlertTriangle, 
  CalendarClock,
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  ArrowRight,
  Activity,
  Calendar,
  Package,
  Euro,
  Eye,
  Flame
} from 'lucide-react';
import { toast } from 'sonner';
import PulseWidget from '../components/PulseWidget';
import { WidgetsSdiVenditeOperativo } from '../components/dashboard/WidgetsSdiVenditeOperativo';
import { WidgetScadenzeIncasso } from '../components/dashboard/WidgetScadenzeIncasso';
import { WidgetFattureContrattoSdi } from '../components/dashboard/WidgetFattureContrattoSdi';
import { formatDate } from '../lib/formatDate';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colors for charts
const COLORS = ['#06b6d4', '#f97316', '#22c55e', '#a855f7', '#ef4444'];
const PRIORITY_COLORS = {
  bassa: '#22c55e',
  media: '#f59e0b', 
  alta: '#f97316',
  urgente: '#ef4444'
};

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [impiantiScadenza, setImpiantiScadenza] = useState([]);
  const [abbonamentiScadenza, setAbbonamentiScadenza] = useState([]);
  const [ticketsRecenti, setTicketsRecenti] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [appuntamentiOggi, setAppuntamentiOggi] = useState([]);
  const [sottoscorta, setSottoscorta] = useState([]);
  const [preventiviAperti, setPreventiviAperti] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const [statsRes, impiantiRes, abbonamentiRes, ticketsRes, appuntamentiRes, sottoscortaRes, preventiviRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/impianti-scadenza?giorni=30`),
        axios.get(`${API}/abbonamenti/scadenza?giorni=30`),
        axios.get(`${API}/tickets`),
        axios.get(`${API}/appuntamenti?data_inizio=${today}&data_fine=${today}`),
        axios.get(`${API}/magazzino/articoli-sotto-scorta`).catch(() => ({ data: [] })),
        axios.get(`${API}/documenti?tipo=preventivo`).catch(() => ({ data: [] })),
      ]);
      
      setStats(statsRes.data);
      setImpiantiScadenza(impiantiRes.data);
      setAbbonamentiScadenza(abbonamentiRes.data);
      setAllTickets(ticketsRes.data);
      setTicketsRecenti(ticketsRes.data.filter(t => t.stato !== 'chiuso').slice(0, 5));
      setAppuntamentiOggi(appuntamentiRes.data || []);
      setSottoscorta(sottoscortaRes.data || []);
      // Filtra solo i preventivi ANCORA IN ATTESA (non accettati)
      const preventivi = preventiviRes.data || [];
      setPreventiviAperti(preventivi.filter(p => p.stato_accettazione !== 'accettato'));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const getTicketsByPriority = () => {
    const openTickets = allTickets.filter(t => t.stato !== 'chiuso');
    const grouped = { bassa: 0, media: 0, alta: 0, urgente: 0 };
    openTickets.forEach(t => {
      if (grouped[t.priorita] !== undefined) {
        grouped[t.priorita]++;
      }
    });
    return Object.entries(grouped).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value,
      color: PRIORITY_COLORS[name]
    }));
  };

  const getTicketsByStatus = () => {
    const grouped = {};
    allTickets.forEach(t => {
      const stato = t.stato || 'aperto';
      grouped[stato] = (grouped[stato] || 0) + 1;
    });
    return Object.entries(grouped).map(([name, value], index) => ({
      name: name.replace('_', ' ').charAt(0).toUpperCase() + name.replace('_', ' ').slice(1),
      value,
      color: COLORS[index % COLORS.length]
    }));
  };

  // Calculate trends (mock data for now - in production would come from API)
  const ticketTrend = stats?.tickets_aperti > 5 ? 'up' : 'down';
  const clientiTrend = stats?.clienti > 0 ? 'up' : 'neutral';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm text-muted-foreground">Caricamento dashboard...</span>
        </div>
      </div>
    );
  }

  const priorityData = getTicketsByPriority();
  const statusData = getTicketsByStatus();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Panoramica delle attività - {new Date().toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
          </p>
        </div>
      </div>

      {/* === WIDGET SDI / VENDITE / OPERATIVO (dati di esempio, in attesa di collegamento API) === */}
      <WidgetsSdiVenditeOperativo />

      <WidgetFattureContrattoSdi />

      <WidgetScadenzeIncasso />

      {/* === ACTION CENTER === Cards azione-prima per accesso rapido alle attività operative del giorno */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="action-center">
        {/* Ticket aperti */}
        <Link to="/tickets?stato=aperto" data-testid="action-tickets-aperti">
          <Card className={`hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer border-l-4 ${(stats?.tickets_aperti || 0) > 0 ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Ticket aperti</p>
                  <p className="text-3xl font-black mt-1">{stats?.tickets_aperti || 0}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {(stats?.tickets_urgenti || 0) > 0 ? <span className="text-red-500 font-semibold">{stats.tickets_urgenti} urgenti</span> : 'Nessun urgente'}
                  </p>
                </div>
                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${(stats?.tickets_aperti || 0) > 0 ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                  <Ticket className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Appuntamenti oggi */}
        <Link to="/calendario" data-testid="action-appuntamenti-oggi">
          <Card className="hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer border-l-4 border-l-cyan-500">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Appuntamenti oggi</p>
                  <p className="text-3xl font-black mt-1">{appuntamentiOggi.length}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {appuntamentiOggi.length === 0 ? 'Nessun appuntamento' : appuntamentiOggi[0]?.ora_inizio ? `Primo alle ${appuntamentiOggi[0].ora_inizio}` : ''}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-md bg-cyan-500/10 text-cyan-500 flex items-center justify-center">
                  <Calendar className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Scadenze 30gg (impianti + abbonamenti) */}
        <Link to="/verifiche-scadenza" data-testid="action-scadenze">
          <Card className={`hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer border-l-4 ${(impiantiScadenza.length + abbonamentiScadenza.length) > 0 ? 'border-l-orange-500' : 'border-l-emerald-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Scadenze 30gg</p>
                  <p className="text-3xl font-black mt-1">{impiantiScadenza.length + abbonamentiScadenza.length}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {impiantiScadenza.length} verifiche · {abbonamentiScadenza.length} abbonamenti
                  </p>
                </div>
                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${(impiantiScadenza.length + abbonamentiScadenza.length) > 0 ? 'bg-orange-500/10 text-orange-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                  <CalendarClock className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Sotto scorta */}
        <Link to="/magazzino" data-testid="action-sottoscorta">
          <Card className={`hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer border-l-4 ${sottoscorta.length > 0 ? 'border-l-red-500' : 'border-l-emerald-500'}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Sotto scorta</p>
                  <p className="text-3xl font-black mt-1">{sottoscorta.length}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    {sottoscorta.length === 0 ? 'Tutto regolare' : sottoscorta.slice(0,1).map(a => a.codice).join(', ') + (sottoscorta.length > 1 ? `, +${sottoscorta.length - 1}` : '')}
                  </p>
                </div>
                <div className={`h-10 w-10 rounded-md flex items-center justify-center ${sottoscorta.length > 0 ? 'bg-red-500/10 text-red-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                  <Package className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {/* Preventivi aperti */}
        <Link to="/documenti?tipo=preventivo" data-testid="action-preventivi">
          <Card className="hover:shadow-lg transition-all hover:-translate-y-0.5 cursor-pointer border-l-4 border-l-violet-500">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Preventivi</p>
                  <p className="text-3xl font-black mt-1">{preventiviAperti.length}</p>
                  <p className="text-[11px] text-muted-foreground mt-1">in attesa risposta</p>
                </div>
                <div className="h-10 w-10 rounded-md bg-violet-500/10 text-violet-500 flex items-center justify-center">
                  <Euro className="h-5 w-5" />
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* === PULSE WIDGET - ultimo evento del sistema in real-time === */}
      <PulseWidget />

      {/* Urgent Alerts */}
      {(stats?.tickets_urgenti > 0 || stats?.tickets_alta_priorita > 0) && (
        <Link to="/tickets?priorita=urgente" data-testid="alerts-section">
          <Card className="border-red-500/50 bg-red-500/5 hover:bg-red-500/10 transition-colors cursor-pointer">
            <CardContent className="py-4">
              <div className="flex items-center gap-4">
                <div className="h-12 w-12 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-red-500" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-600 dark:text-red-400">Attenzione Richiesta</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats?.tickets_urgenti > 0 && `${stats.tickets_urgenti} ticket urgenti`}
                    {stats?.tickets_urgenti > 0 && stats?.tickets_alta_priorita > 0 && ' • '}
                    {stats?.tickets_alta_priorita > 0 && `${stats.tickets_alta_priorita} alta priorità`}
                  </p>
                </div>
                <ArrowRight className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>
        </Link>
      )}

      {/* === HOT LEADS: preventivi visualizzati dal cliente ma ancora non accettati === */}
      {(() => {
        const now = Date.now();
        const ms24h = 24 * 3600 * 1000;
        const ms3g = 3 * 24 * 3600 * 1000;
        const visualizzati = preventiviAperti.filter(p => p.prima_apertura_portale);
        if (visualizzati.length === 0) return null;
        const ultime24h = visualizzati.filter(p => p.ultima_apertura_portale && (now - new Date(p.ultima_apertura_portale).getTime()) < ms24h);
        const stagionati = visualizzati.filter(p => p.ultima_apertura_portale && (now - new Date(p.ultima_apertura_portale).getTime()) > ms3g);
        const isHot = stagionati.length > 0;
        return (
          <Link to="/documenti?tipo=preventivo" data-testid="hot-leads-card">
            <Card className={`${isHot ? 'border-red-500/60 bg-red-500/5 hover:bg-red-500/10' : 'border-indigo-500/60 bg-indigo-500/5 hover:bg-indigo-500/10'} transition-colors cursor-pointer`}>
              <CardContent className="py-4">
                <div className="flex items-center gap-4">
                  <div className={`h-12 w-12 rounded-full ${isHot ? 'bg-red-500/15' : 'bg-indigo-500/15'} flex items-center justify-center`}>
                    {isHot ? <Flame className="h-6 w-6 text-red-500" /> : <Eye className="h-6 w-6 text-indigo-500" />}
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold ${isHot ? 'text-red-600 dark:text-red-400' : 'text-indigo-600 dark:text-indigo-400'}`}>
                      {isHot ? 'Lead caldi da ricontattare' : 'Preventivi visualizzati dal cliente'}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">{visualizzati.length}</span> {visualizzati.length === 1 ? 'preventivo' : 'preventivi'} aperti dal cliente
                      {ultime24h.length > 0 && (<> • <span className="text-indigo-600 font-semibold">{ultime24h.length}</span> nelle ultime 24h</>)}
                      {stagionati.length > 0 && (<> • <span className="text-red-600 font-semibold">{stagionati.length}</span> da oltre 3 giorni</>)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {isHot
                        ? 'Suggerimento: contattali oggi via WhatsApp - il cliente sta valutando ma esita.'
                        : 'Il cliente sta valutando il preventivo. Vuoi accelerare la decisione?'}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })()}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/clienti" data-testid="stat-clienti">
          <Card className="hover:shadow-md transition-all hover:border-cyan-500/50 cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Clienti</p>
                  <p className="text-3xl font-bold mt-2 text-cyan-600">{stats?.clienti || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                  <Users className="h-5 w-5 text-cyan-500" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
                {clientiTrend === 'up' && <TrendingUp className="h-3 w-3 text-green-500" />}
                <span>Totale registrati</span>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/impianti" data-testid="stat-impianti">
          <Card className="hover:shadow-md transition-all hover:border-green-500/50 cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Impianti</p>
                  <p className="text-3xl font-bold mt-2 text-green-600">{stats?.impianti || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Printer className="h-5 w-5 text-green-500" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-xs">
                {stats?.impianti_scadenza > 0 && (
                  <span className="text-amber-500 font-medium">{stats.impianti_scadenza} in scadenza</span>
                )}
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/tickets" data-testid="stat-tickets-aperti">
          <Card className="hover:shadow-md transition-all hover:border-orange-500/50 cursor-pointer h-full">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tickets Aperti</p>
                  <p className="text-3xl font-bold mt-2 text-orange-600">{stats?.tickets_aperti || 0}</p>
                </div>
                <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <Ticket className="h-5 w-5 text-orange-500" />
                </div>
              </div>
              <div className="flex items-center gap-1 mt-3 text-xs">
                {ticketTrend === 'up' ? (
                  <TrendingUp className="h-3 w-3 text-amber-500" />
                ) : (
                  <TrendingDown className="h-3 w-3 text-green-500" />
                )}
                <span className="text-muted-foreground">Da gestire</span>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Card data-testid="stat-tickets-chiusi" className="h-full">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Chiusi (Mese)</p>
                <p className="text-3xl font-bold mt-2 text-purple-600">{stats?.tickets_chiusi_mese || 0}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <CheckCircle2 className="h-5 w-5 text-purple-500" />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-3 text-xs text-muted-foreground">
              <Activity className="h-3 w-3" />
              <span>Completati questo mese</span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tickets by Priority */}
        <Card data-testid="chart-tickets-priority">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Ticket className="h-4 w-4 text-orange-500" />
              Tickets per Priorità
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px] flex items-center">
              {priorityData.some(d => d.value > 0) ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={priorityData.filter(d => d.value > 0)}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {priorityData.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="w-full text-center text-muted-foreground text-sm">
                  Nessun ticket aperto
                </div>
              )}
              <div className="flex flex-col gap-2 ml-4">
                {priorityData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2 text-sm">
                    <div className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-muted-foreground">{item.name}</span>
                    <span className="font-medium ml-auto">{item.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets by Status */}
        <Card data-testid="chart-tickets-status">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Activity className="h-4 w-4 text-cyan-500" />
              Tickets per Stato
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[200px]">
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={statusData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      tick={{ fontSize: 12 }} 
                      width={100}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--background))', 
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }} 
                    />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {statusData.map((entry) => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
                  Nessun dato disponibile
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today's Appointments */}
        <Card data-testid="appuntamenti-oggi">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4 text-green-500" />
                Appuntamenti Oggi
              </CardTitle>
              <Link to="/calendario">
                <Button variant="ghost" size="sm" className="h-7 text-xs">
                  Vedi tutti
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {appuntamentiOggi.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nessun appuntamento oggi</p>
            ) : (
              <div className="space-y-3">
                {appuntamentiOggi.slice(0, 4).map((app) => (
                  <div key={app.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                    <div 
                      className="h-2 w-2 rounded-full" 
                      style={{ backgroundColor: app.tecnico_colore || '#06b6d4' }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{app.titolo}</p>
                      <p className="text-xs text-muted-foreground">{app.ora_inizio} - {app.tecnico_nome}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scadenze Verifiche */}
        <Card data-testid="scadenze-verifiche">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-500" />
                Verifiche in Scadenza
              </CardTitle>
              <Link to="/verifiche-scadenza">
                <Button variant="ghost" size="sm" className="h-7 text-xs">
                  Vedi tutte
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {impiantiScadenza.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nessuna scadenza imminente</p>
            ) : (
              <div className="space-y-3">
                {impiantiScadenza.slice(0, 4).map((impianto) => {
                  const isOverdue = new Date(impianto.data_scadenza_verifica) < new Date();
                  return (
                    <Link 
                      key={impianto.id} 
                      to={`/impianti/${impianto.id}`}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{impianto.matricola}</p>
                        <p className="text-xs text-muted-foreground">{impianto.tipo}</p>
                      </div>
                      <Badge 
                        variant={isOverdue ? "destructive" : "outline"} 
                        className="text-[10px] shrink-0"
                      >
                        {formatDate(impianto.data_scadenza_verifica)}
                      </Badge>
                    </Link>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tickets Recenti */}
        <Card data-testid="tickets-recenti">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Ticket className="h-4 w-4 text-orange-500" />
                Tickets Aperti
              </CardTitle>
              <Link to="/tickets">
                <Button variant="ghost" size="sm" className="h-7 text-xs">
                  Vedi tutti
                  <ArrowRight className="h-3 w-3 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {ticketsRecenti.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">Nessun ticket aperto</p>
            ) : (
              <div className="space-y-3">
                {ticketsRecenti.map((ticket) => (
                  <Link 
                    key={ticket.id} 
                    to={`/tickets/${ticket.id}`}
                    className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">
                        <span className="text-muted-foreground">#{ticket.numero}</span> {ticket.oggetto}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{ticket.descrizione?.substring(0, 40)}...</p>
                    </div>
                    <Badge 
                      className={`ml-2 text-[10px] shrink-0 priority-${ticket.priorita}`}
                      style={{ 
                        backgroundColor: PRIORITY_COLORS[ticket.priorita] + '20',
                        color: PRIORITY_COLORS[ticket.priorita],
                        border: `1px solid ${PRIORITY_COLORS[ticket.priorita]}40`
                      }}
                    >
                      {ticket.priorita}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
